import React, { useState } from 'react';

const ResourceCard = ({ resource }) => {
  const [imageError, setImageError] = useState(false);

  const getResourceIcon = (resourceType) => {
    const type = resourceType?.toLowerCase() || '';
    
    if (type.includes('ec2') || type.includes('instance')) {
      return '/aws-icons/Compute/EC2.svg';
    } else if (type.includes('rds') || type.includes('database')) {
      return '/aws-icons/Database/RDS.svg';
    } else if (type.includes('alb') || type.includes('application-load-balancer')) {
      return '/aws-icons/Networking-Content-Delivery/Application-Load-Balancer.svg';
    } else if (type.includes('elb') || type.includes('load-balancer')) {
      return '/aws-icons/Networking-Content-Delivery/Elastic-Load-Balancing.svg';
    } else if (type.includes('lambda')) {
      return '/aws-icons/Compute/Lambda.svg';
    } else if (type.includes('s3') || type.includes('bucket')) {
      return '/aws-icons/Storage/Simple-Storage-Service.svg';
    } else if (type.includes('cloudfront')) {
      return '/aws-icons/Networking-Content-Delivery/CloudFront.svg';
    } else if (type.includes('iam')) {
      return '/aws-icons/Security-Identity-Compliance/Identity-and-Access-Management.svg';
    }
    
    return null;
  };

  const getResourceColor = (resourceType) => {
    const type = resourceType?.toLowerCase() || '';
    
    if (type.includes('ec2') || type.includes('instance')) {
      return { border: 'border-orange-500', bg: 'bg-orange-50', text: 'text-orange-700' };
    } else if (type.includes('rds') || type.includes('database')) {
      return { border: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-700' };
    } else if (type.includes('alb') || type.includes('load-balancer')) {
      return { border: 'border-purple-500', bg: 'bg-purple-50', text: 'text-purple-700' };
    } else if (type.includes('lambda')) {
      return { border: 'border-orange-500', bg: 'bg-orange-50', text: 'text-orange-700' };
    } else if (type.includes('s3')) {
      return { border: 'border-green-600', bg: 'bg-green-50', text: 'text-green-700' };
    } else if (type.includes('cloudfront')) {
      return { border: 'border-purple-500', bg: 'bg-purple-50', text: 'text-purple-700' };
    } else if (type.includes('iam')) {
      return { border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700' };
    }
    
    return { border: 'border-gray-500', bg: 'bg-gray-50', text: 'text-gray-700' };
  };

  const getStatusColor = (state) => {
    const status = state?.toLowerCase() || '';
    
    if (status === 'running' || status === 'available' || status === 'active') {
      return 'bg-green-500';
    } else if (status === 'stopped' || status === 'unavailable') {
      return 'bg-red-500';
    } else if (status === 'pending' || status === 'stopping') {
      return 'bg-yellow-500';
    }
    
    return 'bg-gray-400';
  };

  const icon = getResourceIcon(resource.type);
  const colors = getResourceColor(resource.type);

  return (
    <div 
      className={`${colors.border} ${colors.bg} border-3 rounded-lg p-3 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer`}
      title={resource.id}
    >
      {/* Header with Icon and Status */}
      <div className="flex items-start justify-between mb-2">
        {icon && !imageError ? (
          <img 
            src={icon}
            alt={resource.type}
            className="w-10 h-10"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className={`w-10 h-10 ${colors.bg} rounded flex items-center justify-center`}>
            <span className={`text-xs font-bold ${colors.text}`}>
              {resource.type?.substring(0, 3).toUpperCase() || '?'}
            </span>
          </div>
        )}
        
        {resource.state && (
          <div className={`w-2 h-2 rounded-full ${getStatusColor(resource.state)}`} title={resource.state}></div>
        )}
      </div>

      {/* Resource Name */}
      <div className="font-semibold text-sm text-gray-900 mb-1 truncate" title={resource.name}>
        {resource.name || 'Unnamed Resource'}
      </div>

      {/* Resource Type */}
      <div className="text-xs text-gray-600 mb-1">
        {resource.instanceType || resource.engine || resource.type}
      </div>

      {/* Resource ID */}
      <div className="text-xs text-gray-500 font-mono truncate mb-2" title={resource.id}>
        {resource.id}
      </div>

      {/* Additional Info */}
      <div className="text-xs text-gray-600 space-y-1">
        {resource.privateIp && (
          <div className="truncate" title={resource.privateIp}>
            <span className="font-semibold">Private:</span> {resource.privateIp}
          </div>
        )}
        {resource.publicIp && (
          <div className="truncate" title={resource.publicIp}>
            <span className="font-semibold">Public:</span> {resource.publicIp}
          </div>
        )}
        {resource.endpoint && (
          <div className="truncate" title={resource.endpoint}>
            <span className="font-semibold">Endpoint:</span> {resource.endpoint}
          </div>
        )}
        {resource.port && (
          <div>
            <span className="font-semibold">Port:</span> {resource.port}
          </div>
        )}
        {resource.size && (
          <div>
            <span className="font-semibold">Size:</span> {resource.size}
          </div>
        )}
      </div>

      {/* Tags */}
      {resource.tags && Object.keys(resource.tags).length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className="flex flex-wrap gap-1">
            {Object.entries(resource.tags).slice(0, 2).map(([key, value]) => (
              <span 
                key={key}
                className={`px-2 py-0.5 ${colors.bg} ${colors.text} text-xs rounded`}
                title={`${key}: ${value}`}
              >
                {key}: {String(value).substring(0, 10)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceCard;
